import React, {
  useState,
  useEffect,
  useRef,
  FC,
  ChangeEvent,
  FormEvent,
} from "react";
import Button from "../../components/ui/Button";
import { ContactFormData, ContactFormErrors } from "../../types/components";
import ReCAPTCHA from "react-google-recaptcha";
import userService from "../../services/user.service";
import Toast from "../../components/ui/Toast";
import { useNavigate, Link } from "react-router-dom";
const Contact: FC = () => {
  const [formData, setFormData] = useState<ContactFormData>({
    fullName: "",
    email: "",
    subject: "",
    mobilenumber: "",
    message: "",
  });
  const [errors, setErrors] = useState<ContactFormErrors>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const heroRef = useRef<HTMLDivElement>(null);
  const recaptchaRef = useRef<ReCAPTCHA | null>(null);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [toast, setToast] = useState<{
    message: string;
    type: "success" | "error" | "warning" | "info";
  } | null>(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const navigate = useNavigate();
  // Animation on scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (heroRef.current) {
      observer.observe(heroRef.current);
    }

    return () => {
      if (heroRef.current) {
        observer.unobserve(heroRef.current);
      }
    };
  }, []);
  

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (errors[name as keyof ContactFormErrors]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handlemobileNumberChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (errors[name as keyof ContactFormErrors]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: ContactFormErrors = {};

    if (!formData.fullName.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }

    if (!formData.subject.trim()) {
      newErrors.subject = "Subject is required";
    }

    if (!formData.message.trim()) {
      newErrors.message = "Message is required";
    }

    if (!formData.mobilenumber) {
      newErrors.mobileNumber = "Mobile number is required";
    } else if (formData.mobilenumber.replace(/\D/g, "").length < 10) {
      newErrors.mobileNumber = "Mobile number must be at least 10 digits";
    }else if (formData.mobilenumber.replace(/\D/g, "").length > 10) {
      newErrors.mobileNumber = "Mobile number must be exactly 10 digits";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleRecaptcha = (token: string | null) => setCaptchaToken(token);
  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    try {
      if (!captchaToken) {
        setToast({ message: "Please complete the CAPTCHA.", type: "warning" });
        return;
      }
      const contactUsData = {
        fullName: formData.fullName,
        email: formData.email,
        subject: formData.subject,
        mobilenumber: formData.mobilenumber,
        message: formData.message,
        recaptchaToken: captchaToken,
      };
      const response = await userService.addContactusDetail(contactUsData);
      setShowSuccessMessage(true);
      if (!response) {
        setToast({
          message: "Error sending message. Please try again.",
          type: "error",
        });
      } else {
        if (recaptchaRef.current) {
          recaptchaRef.current.reset(); // Reset reCAPTCHA
        }
        setToast({ message: "Message sent successfully...", type: "success" });
        setTimeout(() => {
          navigate("/", {
            state: { message: "Message sent successfully...", type: "success" },
          });
        }, 2000);
      }
      setFormData({
        fullName: "",
        email: "",
        subject: "",
        message: "",
        mobilenumber: "",
      });
    } catch (error) {
      console.error("Error submitting contact form:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section
        ref={heroRef}
        className="relative pt-20 pb-20 md:pt-24 md:pb-24 overflow-hidden"
      >
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className={`text-center transition-all duration-1000 ${
              isVisible
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-8"
            }`}
          >
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-primary-black mb-6 leading-tight">
              Get in
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-teaGreen to-primary-lightGreen">
                {" "}
                Touch
              </span>
            </h1>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto mb-10 leading-relaxed">
              Have questions about Dhoondh? We'd love to hear from you. Send us
              a message and we'll respond as soon as possible.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/faq">
                <Button
                  variant="outline"
                  size="lg"
                  className="px-8 py-4 text-lg text-primary-black"
                >
                  View FAQ
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="relative pb-20 md:pb-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-16 lg:gap-20">
            {/* Contact Form */}
            <div className="order-2 xl:order-1">
              <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 md:p-10 lg:p-12 shadow-xl border border-gray-100">
                <div className="mb-10">
                  <h2 className="text-3xl font-bold text-primary-black mb-4">
                    Send us a message
                  </h2>
                  <p className="text-gray-600 text-lg">
                    We typically respond within 24 hours
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="block text-sm font-semibold text-gray-700">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleChange}
                        className={`w-full px-4 py-3 rounded-xl border-2 transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-primary-teaGreen/20 ${
                          // Validation fix: Check 'errors.fullName'
                          errors.name
                            ? "border-red-300 focus:border-red-500"
                            : "border-gray-200 focus:border-primary-teaGreen"
                        }`}
                        placeholder="Your full name"
                      />
                      {/* Validation fix: Check 'errors.fullName' */}
                      {errors.name && (
                        <p className="text-sm text-red-600 flex items-center gap-1">
                          <svg
                            className="w-4 h-4"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                              clipRule="evenodd"
                            />
                          </svg>
                          {/* Validation fix: Show 'errors.fullName' */}
                          {errors.name}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <label className="block text-sm font-semibold text-gray-700">
                        Mobile number *
                      </label>
                      <input
                        type="tel"
                        inputMode="numeric"
                        name="mobilenumber"
                        value={formData.mobilenumber}
                        onChange={handleChange}
                        className={`w-full px-4 py-3 rounded-xl border-2 transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-primary-teaGreen/20 ${
                          errors.mobileNumber
                            ? "border-red-300 focus:border-red-500"
                            : "border-gray-200 focus:border-primary-teaGreen"
                        }`}
                        placeholder="Your mobile number"
                      />
                      {errors.mobileNumber && (
                        <p className="text-sm text-red-600 flex items-center gap-1">
                          <svg
                            className="w-4 h-4"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                              clipRule="evenodd"
                            />
                          </svg>
                          {errors.mobileNumber}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 rounded-xl border-2 transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-primary-teaGreen/20 ${
                        // Validation fix: Check 'errors.email'
                        errors.email
                          ? "border-red-300 focus:border-red-500"
                          : "border-gray-200 focus:border-primary-teaGreen"
                      }`}
                      placeholder="your@email.com"
                    />
                    {errors.email && (
                      <p className="text-sm text-red-600 flex items-center gap-1">
                        <svg
                          className="w-4 h-4"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                        {errors.email}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">
                      Subject *
                    </label>
                    <input
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 rounded-xl border-2 transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-primary-teaGreen/20 ${
                        // Validation fix: Check 'errors.subject'
                        errors.subject
                          ? "border-red-300 focus:border-red-500"
                          : "border-gray-200 focus:border-primary-teaGreen"
                      }`}
                      placeholder="What's this about?"
                    />
                    {errors.subject && (
                      <p className="text-sm text-red-600 flex items-center gap-1">
                        <svg
                          className="w-4 h-4"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                        {errors.subject}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">
                      Message *
                    </label>
                    <textarea
                      name="message"
                      rows={6}
                      value={formData.message}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 rounded-xl border-2 transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-primary-teaGreen/20 ${
                        // Validation fix: Check 'errors.message'
                        errors.message
                          ? "border-red-300 focus:border-red-500"
                          : "border-gray-200 focus:border-primary-teaGreen"
                      }`}
                      placeholder="Tell us more about your inquiry..."
                    />
                    {errors.message && (
                      <p className="text-sm text-red-600 flex items-center gap-1">
                        <svg
                          className="w-4 h-4"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                        {errors.message}
                      </p>
                    )}
                  </div>
                  <ReCAPTCHA
                    sitekey="6Lfd2OIrAAAAAArv7w-qKCyNCHUlbcbsZ7e04lZR"
                    onChange={handleRecaptcha}
                    ref={recaptchaRef}
                  />
                  <Button
                    type="submit"
                    size="lg"
                    className="w-full py-4 text-lg font-semibold"
                    loading={loading}
                    disabled={!captchaToken || loading}
                  >
                    {loading ? "Sending Message..." : "Send Message"}
                  </Button>
                </form>
              </div>
            </div>
            {toast && (
              <Toast
                message={toast.message}
                type={toast.type}
                position="top-right"
                duration={3000}
                onClose={() => setToast(null)}
                show
              />
            )}

            {/* Contact Information */}
            <div className="order-1 xl:order-2">
              <div className="space-y-10">
                <div>
                  <h2 className="text-3xl font-bold text-primary-black mb-4">
                    Get in touch
                  </h2>
                  <p className="text-lg text-gray-600 mb-8">
                    Choose your preferred way to reach us
                  </p>
                </div>

                {/* Contact Cards */}
                <div className="space-y-8">
                  <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 border border-gray-100 hover:shadow-lg transition-all duration-300 group">
                    <div className="flex items-start gap-4">
                      <div className="bg-brand-100 w-14 h-14 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                        <svg
                          className="h-7 w-7 text-brand-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                          />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-primary-black mb-2">
                          Email Us
                        </h3>
                        <p className="text-gray-600 mb-2">
                          Send us an email anytime
                        </p>
                        <div className="space-y-1">
                          <a
                            href="mailto:operations@dhoondh.in"
                            className="block text-brand-600 hover:text-brand-700 underline font-semibold"
                          >
                            operations@dhoondh.in
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 border border-gray-100 hover:shadow-lg transition-all duration-300 group">
                    <div className="flex items-start gap-4">
                      <div className="bg-brand-100 w-14 h-14 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                        <svg
                          className="h-7 w-7 text-brand-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                          />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-primary-black mb-2">
                          Call Us
                        </h3>
                        <p className="text-gray-600 mb-2">
                          Speak with our team directly
                        </p>
                        <div className="space-y-1">
                          <a
                            href="tel:+919316846322"
                            className="block text-brand-600 hover:text-brand-700 underline font-semibold"
                          >
                            +91 93168 46322
                          </a>
                          <p className="text-sm text-gray-500">
                            Mon-Fri 10AM-6PM IST
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 border border-gray-100 hover:shadow-lg transition-all duration-300 group">
                    <div className="flex items-start gap-4">
                      <div className="bg-brand-100 w-14 h-14 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                        <svg
                          className="h-7 w-7 text-brand-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-primary-black mb-2">
                          Visit Us
                        </h3>
                        <p className="text-gray-600 mb-2">
                          Come say hello at our office
                        </p>
                        <div className="space-y-1">
                          <p className="text-brand-600 font-semibold">
                            AMPS DHOONDH RECRUITMENT LLP
                          </p>
                          <p className="text-sm text-gray-600">
                            510, Serene Centrum
                          </p>
                          <p className="text-sm text-gray-600">
                            Laxmipura, Vadodara – 390021
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Social Media */}
                <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 border border-gray-100">
                  <h3 className="text-xl font-bold text-primary-black mb-4">
                    Follow us
                  </h3>
                  <p className="text-gray-600 mb-6">
                    Stay updated with our latest news and updates
                  </p>
                  <div className="flex flex-wrap gap-4">
                    <a
                      href="https://www.instagram.com/dhoondh.in?igsh=MWp0OXN5aGE2MWZuYw%3D%3D&utm_source=qr"
                      className="text-gray-500 hover:text-green-500 transition-colors"
                      aria-label="Instagram"
                    >
                      <svg
                        className="h-6 w-6"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path d="M7 2C4.243 2 2 4.243 2 7v10c0 2.757 2.243 5 5 5h10c2.757 0 5-2.243 5-5V7c0-2.757-2.243-5-5-5H7zm10 2c1.654 0 3 1.346 3 3v10c0 1.654-1.346 3-3 3H7c-1.654 0-3-1.346-3-3V7c0-1.654 1.346-3 3-3h10zm-5 3a6 6 0 100 12 6 6 0 000-12zm0 2a4 4 0 110 8 4 4 0 010-8zm6.5-.75a1.25 1.25 0 11-2.5 0 1.25 1.25 0 012.5 0z" />
                      </svg>
                    </a>
                    <a
                      href="https://www.linkedin.com/company/dhoondh-ltd/?viewAsMember=true"
                      className="text-gray-500 hover:text-green-500 transition-colors"
                      aria-label="LinkedIn"
                    >
                      <svg
                        className="h-5 w-5"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                      </svg>
                    </a>
                    <a
                      href="https://wa.me/919316846322"
                      className="text-gray-500 hover:text-green-500 transition-colors"
                      aria-label="WhatsApp"
                    >
                      <svg
                        className="h-5 w-5"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path d="M20.52 3.48A11.94 11.94 0 0012.06.03C5.64.03.45 5.22.45 11.64c0 2.05.54 4.05 1.57 5.82L.02 24l6.69-2.02a11.7 11.7 0 005.35 1.34h.01c6.43 0 11.62-5.19 11.62-11.61 0-3.1-1.21-6.02-3.17-8.23zM12.07 21.2h-.01a9.55 9.55 0 01-4.86-1.31l-.35-.2-3.97 1.2 1.18-3.86-.23-.36a9.6 9.6 0 01-1.45-5.23C2.38 6.41 6.45 2.34 12.06 2.34c2.57 0 4.98 1 6.8 2.82a9.53 9.53 0 012.8 6.78c0 5.61-4.57 10.26-10.59 9.26z" />
                        <path d="M17.16 14.22c-.29-.14-1.72-.85-1.98-.95-.27-.1-.47-.14-.67.14-.2.29-.77.95-.94 1.15-.17.19-.35.21-.64.07-.29-.14-1.22-.45-2.33-1.44-.86-.77-1.44-1.72-1.6-2.01-.17-.29-.02-.45.12-.59.12-.12.29-.31.43-.46.15-.15.19-.26.29-.43.1-.19.05-.34-.02-.48-.07-.14-.67-1.62-.92-2.21-.24-.58-.48-.5-.67-.5l-.57-.01c-.2 0-.48.07-.73.34-.25.26-.96.94-.96 2.29s.98 2.66 1.12 2.85c.14.19 1.92 2.94 4.65 4.13.65.28 1.16.44 1.56.56.65.21 1.24.18 1.7.11.52-.08 1.72-.7 1.97-1.37.24-.67.24-1.23.17-1.37-.07-.14-.26-.22-.55-.36z" />
                      </svg>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
